/**
 * GeneralFeedbackDialog Component
 *
 * Dialog for providing general feedback on an offer or job.
 * Triggered by the "Provide Feedback" button.
 */
import { TextField } from '@mui/material';
import { SendMessageRequest } from 'features/feedback/types';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DialogSubmitPanel } from 'shared/components';
import Dialog from 'shared/components/layout/dialog/Dialog';

const MAX_FREE_TEXT_LENGTH = 1000;

interface GeneralFeedbackDialogProps {
    open: boolean;
    onClose: () => void;
    isLoading: boolean;
    error: Error | null;
    onSubmit: (data: SendMessageRequest['payload']) => void;
}

export default function GeneralFeedbackDialog({
    open,
    onClose,
    onSubmit,
    isLoading,
    error
}: GeneralFeedbackDialogProps) {
    const { t } = useTranslation('feedback');
    const [ freeText, setFreeText ] = useState('');
    const [ validationError, setValidationError ] = useState<string | null>(null);

    useEffect(() => {
        if (open) {
            setFreeText('');
            setValidationError(null);
        }
    }, [ open ]);

    const handleFreeTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFreeText(event.target.value);
    };

    const handleSubmit = useCallback(() => {
        if (freeText.trim().length === 0) {
            setValidationError(t('generalDialog.freeText.validationRequired'));

            return;
        }

        onSubmit({
            freeText: freeText.trim()
        });
    }, [ freeText, onSubmit, t ]);
    const errorText = error ? t('toast.error') : undefined;

    return (
        <Dialog variant="dialog"
            open={ open }
            onClose={ onClose }
            title={ t('generalDialog.title') }
            subtitle={ t('generalDialog.subtitle') }
        >
            <TextField onChange={ handleFreeTextChange }
                value={ freeText }
                label={ t('generalDialog.freeText.label') }
                placeholder={ t('generalDialog.freeText.placeholder') }
                name="freeText"
                error={ Boolean(validationError) }
                helperText={ validationError }
                multiline
                fullWidth
                minRows={ 3 }
                sx={{ mt: 1 }}
                slotProps={{
                    htmlInput: {
                        maxLength: MAX_FREE_TEXT_LENGTH
                    }
                }} />

            <DialogSubmitPanel onSubmit={ handleSubmit }
                isSubmitting={ isLoading } 
                label={ t('generalDialog.actions.submit') }
                error={ errorText } />
        </Dialog>
    );
}
